import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Plus, Search, Pencil, Trash2, Dumbbell, X, Save, Filter, Image, ExternalLink, Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle } from "lucide-react"
import { getExercises, createExercise, updateExercise, deleteExercise } from "../../api/exercises"
import { parseExercisesFromExcel, downloadExerciseTemplate, type ParsedExercise } from "../../utils/parseExcel"
import type { Exercise } from "../../types"
import ExerciseMedia from "../ExerciseMedia"

const MUSCLE_GROUPS = ["Legs", "Chest", "Back", "Shoulders", "Arms", "Core"]

const MUSCLE_COLORS: Record<string, string> = {
  legs: "bg-green-500/20 text-green-400 border-green-500/30",
  chest: "bg-red-500/20 text-red-400 border-red-500/30",
  back: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  shoulders: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  arms: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  core: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
}

function getMuscleColor(group: string) {
  return MUSCLE_COLORS[group?.toLowerCase()] ?? "bg-white/10 text-white/60 border-white/20"
}

export default function ExerciseLibraryTab() {
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [filterGroup, setFilterGroup] = useState("")

  // Excel import state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showImport, setShowImport] = useState(false)
  const [parsedRows, setParsedRows] = useState<ParsedExercise[]>([])
  const [importing, setImporting] = useState(false)
  const [importDone, setImportDone] = useState<{ ok: number; fail: number } | null>(null)

  // Modal form state
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [name, setName] = useState("")
  const [muscleGroup, setMuscleGroup] = useState("Legs")
  const [equipment, setEquipment] = useState("")
  const [description, setDescription] = useState("")
  const [gifUrl, setGifUrl] = useState("")
  const [saving, setSaving] = useState(false)

  // GIF preview modal
  const [previewGif, setPreviewGif] = useState<Exercise | null>(null)

  useEffect(() => {
    getExercises()
      .then((data) => setExercises(Array.isArray(data) ? data : []))
      .catch(() => setExercises([]))
      .finally(() => setLoading(false))
  }, [])

  function openNew() {
    setEditingId(null)
    setName("")
    setMuscleGroup("Legs")
    setEquipment("")
    setDescription("")
    setGifUrl("")
    setShowModal(true)
  }

  function openEdit(ex: Exercise) {
    setEditingId(ex.id)
    setName(ex.name)
    setMuscleGroup(ex.muscle_group)
    setEquipment(ex.equipment ?? "")
    setDescription(ex.description ?? "")
    setGifUrl(ex.gif_url ?? "")
    setShowModal(true)
  }

  async function handleSave() {
    if (!name.trim()) return
    setSaving(true)
    try {
      const params = {
        name,
        muscle_group: muscleGroup,
        equipment: equipment || undefined,
        description: description || undefined,
        gif_url: gifUrl || undefined,
      }
      if (editingId) {
        const updated = await updateExercise(editingId, params)
        setExercises((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))
      } else {
        const created = await createExercise(params)
        setExercises((prev) => [...prev, created])
      }
      setShowModal(false)
    } finally {
      setSaving(false)
    }
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const rows = await parseExercisesFromExcel(file)
    setParsedRows(rows)
    setImportDone(null)
    setShowImport(true)
    e.target.value = ""
  }

  async function handleImport() {
    setImporting(true)
    let ok = 0, fail = 0
    for (const row of parsedRows.filter((r) => !r.error)) {
      try {
        const created = await createExercise({
          name: row.name,
          muscle_group: row.muscle_group,
          equipment: row.equipment,
          description: row.description,
          gif_url: row.gif_url,
        })
        setExercises((prev) => [...prev, created])
        ok++
      } catch {
        fail++
      }
    }
    setImporting(false)
    setImportDone({ ok, fail })
  }

  async function handleDelete(id: number) {
    if (!confirm("¿Eliminar este ejercicio? Puede afectar rutinas existentes.")) return
    await deleteExercise(id)
    setExercises((prev) => prev.filter((e) => e.id !== id))
  }

  const filtered = exercises.filter((ex) => {
    const matchSearch =
      !search ||
      ex.name.toLowerCase().includes(search.toLowerCase()) ||
      ex.equipment?.toLowerCase().includes(search.toLowerCase())
    const matchGroup = !filterGroup || ex.muscle_group.toLowerCase() === filterGroup.toLowerCase()
    return matchSearch && matchGroup
  })

  const grouped = filtered.reduce<Record<string, Exercise[]>>((acc, ex) => {
    const key = ex.muscle_group
    ;(acc[key] = acc[key] || []).push(ex)
    return acc
  }, {})

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Dumbbell size={40} className="text-primary animate-pulse-glow" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2
            className="text-sm font-semibold text-white uppercase tracking-widest"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Biblioteca de Ejercicios
          </h2>
          <p className="text-xs text-muted">{exercises.length} ejercicios registrados</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar ejercicio..."
              className="rounded-lg border border-white/10 bg-surface-elevated pl-9 pr-3 py-2 text-sm text-white focus:border-primary w-48"
            />
          </div>
          <div className="relative">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="rounded-lg border border-white/10 bg-surface-elevated pl-9 pr-3 py-2 text-sm text-white focus:border-primary appearance-none"
            >
              <option value="">Todos los músculos</option>
              {MUSCLE_GROUPS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2 text-sm font-medium text-muted hover:text-white hover:border-white/40 transition"
          >
            <Upload size={15} />
            Importar Excel
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileSelect} />
          <button
            onClick={openNew}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan"
          >
            <Plus size={16} />
            Nuevo Ejercicio
          </button>
        </div>
      </div>

      {/* Exercise Groups */}
      {Object.keys(grouped).length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="gradient-card border border-white/10 rounded-xl p-12 text-center"
        >
          <Dumbbell size={48} className="mx-auto mb-4 text-muted opacity-30" />
          <p className="text-muted text-sm">
            {search || filterGroup ? "No hay ejercicios con ese filtro." : "No hay ejercicios aún."}
          </p>
        </motion.div>
      ) : (
        Object.entries(grouped).map(([group, exs], groupIdx) => (
          <motion.div
            key={group}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: groupIdx * 0.05 }}
          >
            <div className="mb-3 flex items-center gap-2">
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${getMuscleColor(group)}`}>
                {group}
              </span>
              <span className="text-xs text-muted">{exs.length} ejercicio{exs.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {exs.map((ex) => (
                <div
                  key={ex.id}
                  className="gradient-card border border-white/10 rounded-lg px-4 py-3 flex items-start gap-3 hover:border-white/20 transition-colors cursor-pointer"
                  onClick={() => openEdit(ex)}
                >
                  {ex.gif_url ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setPreviewGif(ex) }}
                      className="shrink-0 rounded-lg overflow-hidden border border-white/10 hover:border-primary/50 transition-colors"
                      title="Ver GIF"
                    >
                      <ExerciseMedia
                        src={ex.gif_url}
                        alt={ex.name}
                        className="h-12 w-12 object-cover"
                        autoPlay={false}
                      />
                    </button>
                  ) : (
                    <div className="pt-0.5 p-1.5 rounded-lg bg-primary/10 shrink-0">
                      <Dumbbell size={14} className="text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{ex.name}</p>
                    {ex.equipment && (
                      <p className="text-xs text-muted truncate">{ex.equipment}</p>
                    )}
                    {ex.description && (
                      <p className="text-xs text-muted/70 mt-0.5 truncate">{ex.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {ex.gif_url && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewGif(ex) }}
                        className="p-1.5 rounded text-muted hover:text-primary hover:bg-primary/10 transition"
                        title="Ver GIF"
                      >
                        <Image size={12} />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); openEdit(ex) }}
                      className="p-1.5 rounded text-muted hover:text-primary hover:bg-primary/10 transition"
                      title="Editar"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(ex.id) }}
                      className="p-1.5 rounded text-muted hover:text-danger hover:bg-danger/10 transition"
                      title="Eliminar"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))
      )}

      {/* Excel Import Modal */}
      <AnimatePresence>
        {showImport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => !importing && setShowImport(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              className="gradient-card border border-white/20 rounded-2xl p-6 max-w-2xl w-full space-y-4 max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={18} className="text-primary" />
                  <h3 className="text-lg font-semibold text-white">Importar Ejercicios</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={downloadExerciseTemplate}
                    className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-muted hover:text-white transition"
                  >
                    <Download size={12} />
                    Descargar Plantilla
                  </button>
                  <button onClick={() => setShowImport(false)} className="p-2 rounded-lg text-muted hover:text-white hover:bg-white/10 transition">
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Summary */}
              {parsedRows.length > 0 && (
                <div className="flex gap-3 text-sm shrink-0">
                  <span className="flex items-center gap-1.5 text-green-400">
                    <CheckCircle size={14} />
                    {parsedRows.filter((r) => !r.error).length} válidos
                  </span>
                  {parsedRows.some((r) => r.error) && (
                    <span className="flex items-center gap-1.5 text-red-400">
                      <AlertCircle size={14} />
                      {parsedRows.filter((r) => r.error).length} con error
                    </span>
                  )}
                </div>
              )}

              {/* Preview table */}
              <div className="overflow-auto flex-1 rounded-lg border border-white/10">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10 bg-surface-elevated text-left">
                      <th className="px-3 py-2 text-muted font-medium">Nombre</th>
                      <th className="px-3 py-2 text-muted font-medium">Grupo</th>
                      <th className="px-3 py-2 text-muted font-medium">Equipo</th>
                      <th className="px-3 py-2 text-muted font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((row, i) => (
                      <tr key={i} className={`border-b border-white/5 ${row.error ? "opacity-50" : ""}`}>
                        <td className="px-3 py-2 text-white">{row.name || "—"}</td>
                        <td className="px-3 py-2 text-white">{row.muscle_group || "—"}</td>
                        <td className="px-3 py-2 text-muted">{row.equipment || "—"}</td>
                        <td className="px-3 py-2">
                          {row.error ? (
                            <span className="text-red-400 flex items-center gap-1"><AlertCircle size={11} />{row.error}</span>
                          ) : (
                            <span className="text-green-400 flex items-center gap-1"><CheckCircle size={11} />OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Result message */}
              {importDone && (
                <p className="text-sm text-center shrink-0">
                  <span className="text-green-400 font-semibold">{importDone.ok} importados</span>
                  {importDone.fail > 0 && <span className="text-red-400"> · {importDone.fail} fallaron</span>}
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-2 shrink-0">
                {!importDone ? (
                  <button
                    onClick={handleImport}
                    disabled={importing || parsedRows.filter((r) => !r.error).length === 0}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
                  >
                    <Upload size={14} />
                    {importing ? "Importando..." : `Importar ${parsedRows.filter((r) => !r.error).length} ejercicios`}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowImport(false)}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition"
                  >
                    Listo
                  </button>
                )}
                <button
                  onClick={() => setShowImport(false)}
                  disabled={importing}
                  className="flex items-center gap-2 rounded-lg border border-white/10 px-5 py-2.5 text-sm text-muted hover:text-white transition"
                >
                  Cancelar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create / Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="gradient-card border border-white/20 rounded-2xl p-6 max-w-lg w-full space-y-5"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  {editingId ? "Editar Ejercicio" : "Nuevo Ejercicio"}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg text-muted hover:text-white hover:bg-white/10 transition"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form fields */}
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">Nombre</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nombre del ejercicio"
                    className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">Grupo muscular</label>
                    <select
                      value={muscleGroup}
                      onChange={(e) => setMuscleGroup(e.target.value)}
                      className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
                    >
                      {MUSCLE_GROUPS.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">Equipo</label>
                    <input
                      value={equipment}
                      onChange={(e) => setEquipment(e.target.value)}
                      placeholder="Barra, Mancuernas, etc."
                      className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">Descripción</label>
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descripción breve del ejercicio (opcional)"
                    className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
                  />
                </div>

                {/* GIF URL field */}
                <div>
                  <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted uppercase tracking-wide">
                    <Image size={12} />
                    URL del GIF
                  </label>
                  <input
                    value={gifUrl}
                    onChange={(e) => setGifUrl(e.target.value)}
                    placeholder="https://media.giphy.com/media/.../giphy.gif"
                    className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
                  />
                  <p className="mt-1 text-xs text-muted/60">
                    Pega un link de Giphy, Tenor o cualquier URL de imagen/GIF
                  </p>
                </div>

                {/* GIF Preview */}
                {gifUrl && (
                  <div className="rounded-xl overflow-hidden border border-white/10 bg-black/30">
                    <img
                      src={gifUrl}
                      alt="Preview del GIF"
                      className="w-full object-contain max-h-48"
                      onError={(e) => {
                        const el = e.target as HTMLImageElement
                        el.style.display = "none"
                        el.parentElement!.innerHTML = '<p class="text-xs text-danger p-4 text-center">No se pudo cargar el GIF. Verifica el URL.</p>'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving || !name.trim()}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan disabled:opacity-50"
                >
                  <Save size={14} />
                  {saving ? "Guardando..." : editingId ? "Actualizar" : "Crear Ejercicio"}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="flex items-center gap-2 rounded-lg border border-white/10 px-5 py-2.5 text-sm text-muted hover:text-white transition"
                >
                  Cancelar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* GIF Preview Modal */}
      <AnimatePresence>
        {previewGif && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setPreviewGif(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="gradient-card border border-white/20 rounded-2xl p-6 max-w-lg w-full space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">{previewGif.name}</h3>
                  <p className="text-xs text-muted">{previewGif.muscle_group} {previewGif.equipment ? `· ${previewGif.equipment}` : ""}</p>
                </div>
                <button
                  onClick={() => setPreviewGif(null)}
                  className="p-2 rounded-lg text-muted hover:text-white hover:bg-white/10 transition"
                >
                  <X size={18} />
                </button>
              </div>
              {previewGif.gif_url && (
                <div className="rounded-xl overflow-hidden border border-white/10">
                  <ExerciseMedia
                    src={previewGif.gif_url}
                    alt={previewGif.name}
                    className="w-full object-contain max-h-80"
                  />
                </div>
              )}
              {previewGif.description && (
                <p className="text-sm text-muted">{previewGif.description}</p>
              )}
              <div className="flex items-center gap-3">
                {previewGif.gif_url && (
                  <a
                    href={previewGif.gif_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition"
                  >
                    <ExternalLink size={12} />
                    Abrir en nueva pestaña
                  </a>
                )}
                <button
                  onClick={() => { setPreviewGif(null); openEdit(previewGif) }}
                  className="inline-flex items-center gap-1.5 text-xs text-muted hover:text-white transition"
                >
                  <Pencil size={12} />
                  Editar ejercicio
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
